import type { PuzzleData } from '../types';
import type { PuzzleResult } from '../puzzles/PuzzleGenerator';
import { COLOR_PALETTE } from '../puzzles/ColorSequence';

const GRID_COLORS = [
  '#FF6B6B', '#4ECDC4', '#60A5FA', '#FFE66D',
  '#A78BFA', '#FB923C', '#34D399', '#F472B6',
];

const SHAPE_PATHS: Record<number, string> = {
  0: 'circle',
  1: 'square',
  2: 'triangle',
  3: 'diamond',
  4: 'pentagon',
  5: 'hexagon',
  6: 'star',
  7: 'cross',
};

export class ValidationPanel {
  private container: HTMLElement;
  private panel: HTMLElement;
  private onDecision: (approved: boolean) => void;

  constructor(container: HTMLElement, onDecision: (approved: boolean) => void) {
    this.container = container;
    this.onDecision = onDecision;

    this.panel = document.createElement('div');
    this.panel.className = 'validation-panel hidden';
    this.panel.innerHTML = `
      <div class="validation-content glass-panel">
        <div class="validation-header">VALIDATE OUTPUT</div>
        <div class="validation-grids">
          <div class="validation-side">
            <div class="validation-label" id="val-ref-label">Expected</div>
            <div class="validation-grid" id="val-reference"></div>
          </div>
          <div class="validation-divider"></div>
          <div class="validation-side">
            <div class="validation-label" id="val-out-label">Agent Output</div>
            <div class="validation-grid" id="val-output"></div>
          </div>
        </div>
        <div class="validation-buttons">
          <button class="btn btn-approve interactive" id="val-approve">APPROVE [Space]</button>
          <button class="btn btn-reject interactive" id="val-reject">REJECT [R]</button>
        </div>
      </div>
    `;
    this.container.querySelector('#hud')?.appendChild(this.panel);

    // Wire buttons
    document.getElementById('val-approve')?.addEventListener('click', () => this.decide(true));
    document.getElementById('val-reject')?.addEventListener('click', () => this.decide(false));

    // Keyboard shortcuts
    this.handleKey = this.handleKey.bind(this);
    window.addEventListener('keydown', this.handleKey);
  }

  private handleKey(e: KeyboardEvent): void {
    if (this.panel.classList.contains('hidden')) return;
    if (e.code === 'Space') {
      e.preventDefault();
      this.decide(true);
    } else if (e.code === 'KeyR') {
      e.preventDefault();
      this.decide(false);
    }
  }

  show(puzzle: PuzzleResult): void {
    const refLabel = document.getElementById('val-ref-label')!;
    const outLabel = document.getElementById('val-out-label')!;
    const refGrid = document.getElementById('val-reference')!;
    const outGrid = document.getElementById('val-output')!;

    refLabel.textContent = puzzle.referenceLabel;
    outLabel.textContent = puzzle.outputLabel;

    refGrid.innerHTML = this.renderPuzzle(puzzle.reference);
    outGrid.innerHTML = this.renderPuzzle(puzzle.output);

    this.panel.classList.remove('hidden');
  }

  hide(): void {
    this.panel.classList.add('hidden');
  }

  private decide(approved: boolean): void {
    this.hide();
    this.onDecision(approved);
  }

  private renderPuzzle(data: number[][]): string {
    if (data.length === 0) return '<div class="puzzle-empty">No data</div>';

    // Detect if this is a grid (2D array with multiple cols) or a sequence (1D)
    const isGrid = data.length > 1 && data[0].length > 1;

    if (isGrid) {
      return this.renderGrid(data);
    } else if (data[0].length === 1) {
      return this.renderSequence(data);
    } else {
      return this.renderShapes(data);
    }
  }

  private renderSequence(data: number[][]): string {
    const cells = data.map(([colorIdx]) => {
      const color = COLOR_PALETTE[colorIdx] || GRID_COLORS[colorIdx % GRID_COLORS.length];
      return `<div class="puzzle-cell" style="background:${color};width:32px;height:32px;border-radius:4px;"></div>`;
    });
    return `<div class="puzzle-row">${cells.join('')}</div>`;
  }

  private renderGrid(data: number[][]): string {
    const rows = data.map(row => {
      const cells = row.map(colorIdx => {
        const color = GRID_COLORS[colorIdx % GRID_COLORS.length];
        return `<div class="puzzle-cell" style="background:${color};width:28px;height:28px;border-radius:3px;"></div>`;
      });
      return `<div class="puzzle-row">${cells.join('')}</div>`;
    });
    return rows.join('');
  }

  private renderShapes(data: number[][]): string {
    const cells = data.map(([shapeType, colorIdx]) => {
      const color = GRID_COLORS[colorIdx % GRID_COLORS.length];
      const shape = this.getShapeSVG(shapeType, color);
      return `<div class="puzzle-cell" style="width:36px;height:36px;">${shape}</div>`;
    });
    return `<div class="puzzle-row puzzle-shapes">${cells.join('')}</div>`;
  }

  private getShapeSVG(type: number, color: string): string {
    const paths: Record<number, string> = {
      0: `<circle cx="18" cy="18" r="14" fill="${color}"/>`,
      1: `<rect x="4" y="4" width="28" height="28" fill="${color}"/>`,
      2: `<polygon points="18,2 34,34 2,34" fill="${color}"/>`,
      3: `<polygon points="18,2 34,18 18,34 2,18" fill="${color}"/>`,
      4: `<polygon points="18,2 33,13 28,32 8,32 3,13" fill="${color}"/>`,
      5: `<polygon points="18,2 32,10 32,26 18,34 4,26 4,10" fill="${color}"/>`,
      6: `<polygon points="18,2 22,14 34,14 24,22 28,34 18,26 8,34 12,22 2,14 14,14" fill="${color}"/>`,
      7: `<path d="M14,2 h8 v12 h12 v8 h-12 v12 h-8 v-12 h-12 v-8 h12 z" fill="${color}"/>`,
    };
    return `<svg viewBox="0 0 36 36" width="36" height="36">${paths[type] || paths[0]}</svg>`;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.handleKey);
    this.panel.remove();
  }
}
